import { useState } from 'react'
import { startRegistration } from '@simplewebauthn/browser'
import { LoopApiError } from '@innerloopco/loopcms-core'
import { Button } from '../../components/index.js'
import { api } from '../../lib/api.js'
import { useAuth } from '../../lib/auth.js'
import { CopyButton } from '../account/CopyButton.js'
import { TotpEnrolDialog } from '../account/TotpEnrolDialog.js'
import { SignedOutLayout } from './SignedOutLayout.js'

/**
 * Shown to someone signed in with only a password (a new invitee, or anyone
 * from before passkeys and required two-factor): they add a passkey (one tap)
 * or an authenticator app before they can use the CMS. A passkey's first set
 * of recovery codes is shown here, once; the authenticator dialog shows its own.
 */
export function SetupSignIn() {
  const { user, finishSetup, signOut } = useAuth()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [codes, setCodes] = useState<string[] | null>(null)
  const [totpOpen, setTotpOpen] = useState(false)

  const addPasskey = async () => {
    setBusy(true)
    setError(null)
    try {
      const optionsJSON = await api.passkeyRegisterOptions()
      const response = await startRegistration({ optionsJSON: optionsJSON as never })
      const result = await api.passkeyRegister(response)
      if (result.recoveryCodes) setCodes(result.recoveryCodes)
      else await finishSetup()
    } catch (caught) {
      // Cancelling the browser's prompt lands here too; say so plainly.
      setError(caught instanceof LoopApiError ? caught.message : 'The passkey wasn’t added. Try again, or use an authenticator app.')
    } finally {
      setBusy(false)
    }
  }

  if (codes) {
    return (
      <SignedOutLayout title="Save your recovery codes">
        <div className="stack" style={{ gap: 'var(--space-4)' }}>
          <p className="text-muted" style={{ margin: 0, fontSize: 13 }}>
            If you lose this device, one of these gets you in after your password. Each works once. Keep them somewhere safe — they won’t be shown again.
          </p>
          <div className="lc-recovery-codes" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontFamily: 'var(--font-mono)', fontSize: 13 }}>
            {codes.map((code) => <span key={code}>{code}</span>)}
          </div>
          <CopyButton text={codes.join('\n')} label="Copy all" />
          <Button variant="primary" block onClick={() => void finishSetup()}>I’ve saved these</Button>
        </div>
      </SignedOutLayout>
    )
  }

  return (
    <SignedOutLayout title="Add a second way to sign in">
      <div className="stack" style={{ gap: 'var(--space-4)' }}>
        <p className="text-muted" style={{ margin: 0, fontSize: 13 }}>
          {user ? `${user.email}: to` : 'To'} keep the CMS safe, everyone signs in with a passkey or an authenticator app as well as their password.
        </p>
        <div className="stack" style={{ gap: 'var(--space-2)' }}>
          <Button variant="primary" block disabled={busy} onClick={() => void addPasskey()} style={{ minHeight: 42 }}>
            {busy ? 'Waiting for your device…' : 'Add a passkey'}
          </Button>
          <p className="text-muted" style={{ margin: 0, fontSize: 12 }}>Face ID, Touch ID, Windows Hello or a security key — then you can sign in with just your passkey.</p>
        </div>
        <Button variant="secondary" block disabled={busy} onClick={() => setTotpOpen(true)}>Use an authenticator app</Button>
        {error ? <p role="alert" style={{ margin: 0, fontSize: 13, color: 'var(--color-danger)' }}>{error}</p> : null}
        <Button variant="ghost" onClick={() => void signOut()}>Sign out</Button>
      </div>
      <TotpEnrolDialog
        open={totpOpen}
        onClose={() => {
          setTotpOpen(false)
          // Closed after confirming (Done) or cancelled: re-check; a set-up session stays here.
          void finishSetup()
        }}
      />
    </SignedOutLayout>
  )
}
